import { viewTheme } from '../views/theme'

const HEX = /^#[0-9a-f]{6}$/i

function optionId(option: unknown): string | undefined {
  if (typeof option === 'string') return option
  if (typeof option === 'object' && option !== null) {
    const id = (option as { id?: unknown }).id
    return typeof id === 'string' ? id : undefined
  }
  return undefined
}

function declaredColor(option: unknown): string | undefined {
  const color = typeof option === 'object' && option !== null ? (option as { color?: unknown }).color : undefined
  return typeof color === 'string' && HEX.test(color) ? color : undefined
}

/** A declared option the metadata RETIRED (`retired: true`, monad/select_options.py):
 *  records that have it still show it (label, colour, its group); no picker offers it. */
export function isRetiredOption(option: unknown): boolean {
  return typeof option === 'object' && option !== null && (option as { retired?: unknown }).retired === true
}

/** Is the option with id `key` retired? (False for an undeclared key.) */
export function isRetiredKey(key: string, options: unknown): boolean {
  return Array.isArray(options) && options.some((option) => optionId(option) === key && isRetiredOption(option))
}

/** The options a picker OFFERS: the declared list less its retired options. A
 *  picker still draws each with the FULL list (optionColor / OptionLabel), so
 *  a colour keyed by position never shifts. */
export function offeredOptions<T>(options: T[]): T[] {
  return options.filter((option) => !isRetiredOption(option))
}

/** Is `key` one of the declared options (a `config.options` list)? */
export function isDeclaredOption(key: string, options: unknown): boolean {
  return Array.isArray(options) && options.some((option) => optionId(option) === key)
}

/** One colour per option of a DECLARED list, in list order (see optionColor). */
function colorsForDeclared(options: unknown[]): string[] {
  const palette = viewTheme.identity.palette
  const out: string[] = []
  options.forEach((option, index) => {
    const declared = declaredColor(option)
    if (declared) {
      out.push(declared)
      return
    }
    const previous = out[index - 1]
    const next = declaredColor(options[index + 1])
    // Position in the palette, wrapping past its end; stepped on when that
    // would repeat a NEIGHBOUR's colour (a declared neighbour can hold any
    // palette colour), so adjacent options always read apart.
    let step = 0
    let candidate = palette[index % palette.length]
    while ((candidate === previous || candidate === next) && step < palette.length) {
      step += 1
      candidate = palette[(index + step) % palette.length]
    }
    out.push(candidate)
  })
  return out
}

/**
 * The colour of a select / enum option VALUE (owner decision 2026-09-24:
 * "the hashed colouring of record names has to go. Apply to Enum and Select
 * instead"). A pure function of the attribute's declared options and the
 * value's stable KEY (the stored option id — never its label, never the
 * record), so the same value has the same colour on every surface that is
 * handed the same attribute config: table, record page, kanban, filter chips.
 *
 * - An option that declares a valid `#rrggbb` colour gets it, always.
 * - Otherwise the option's POSITION in the declared list picks from the
 *   identity palette, wrapping past its end, never repeating a neighbour's
 *   colour. Deterministic; appending an option recolours nothing, reordering
 *   the declared list does (stated trade: position is what makes a small
 *   vocabulary read distinct — a key hash over ten colours collides for half
 *   of all four-option lists).
 * - A value not in a declared list (a free or server-resolved vocabulary)
 *   falls back to a colour derived from its key.
 */
export function optionColor(key: string, options?: unknown): string {
  if (Array.isArray(options)) {
    const index = options.findIndex((option) => optionId(option) === key)
    if (index >= 0) return colorsForDeclared(options)[index]
  }
  // FNV-1a over the key: stable across sessions and engines.
  let hash = 0x811c9dc5
  for (const ch of key) {
    hash ^= ch.codePointAt(0) ?? 0
    hash = Math.imul(hash, 0x01000193)
  }
  const palette = viewTheme.identity.palette
  return palette[(hash >>> 0) % palette.length]
}
